import { useEffect, useMemo, useRef, useState, type ChangeEvent, type FormEvent, type SyntheticEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Grow,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField as MuiTextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import NotesIcon from '@mui/icons-material/Notes';
import PersonIcon from '@mui/icons-material/Person';
import AddressAutocomplete from '../../components/AddressAutocomplete';
import DateField from '../../components/DateField';
import FormSection from '../../components/FormSection';
import FormTextField from '../../components/FormTextField';
import PhoneTextField from '../../components/PhoneTextField';
import { getPhoneDigits, isValidPhone, PHONE_INPUT_ERROR } from '../../../shared/phoneInput';
import {
  authFetch,
  getStoredUser,
  hasRole,
  storeAuthSession,
} from '../../utils/auth';
import {
  appendQuickEntryUserToUrl,
  getStoredQuickEntryUser,
  MEDALLION_USERS,
  normalizeQuickEntryUser,
  setStoredQuickEntryUser,
} from '../../utils/quickEntryUser';
import { CAREGIVER_PRIMARY_LANGUAGE_OPTIONS } from '../../../shared/nexusPicklists';

type LeadType = 'Patient' | 'Caregiver';

type FormState = {
  leadType: LeadType;
  patientFirstName: string;
  patientLastName: string;
  patientDateOfBirth: string;
  patientMobile: string;
  patientPhone: string;
  medicaidId: string;
  pcpId: string;
  street: string;
  aptSuite: string;
  city: string;
  state: string;
  zipCode: string;
  caregiverFirstName: string;
  caregiverLastName: string;
  caregiverDateOfBirth: string;
  caregiverMobile: string;
  caregiverPhone: string;
  email: string;
  primaryLanguage: string;
  medallionNotes: string;
  medallionHealthUser: string;
};

type FieldErrors = Partial<Record<keyof FormState, string>>;

type MedcheckData = {
  medicaidIdNumber: string;
  medicaidPayer: string;
  medicaidPlanCode: string;
  medcheckMedicaidStatus: string;
  medcheckStatus: string;
  medicaidCodes: string;
  medcheckMedicaidResponse: string;
  medcheckMedicareResponse: string;
  medcheckSyncDate: string;
  medcheckSyncStatus: string;
  medicarePlanName: string;
  recertificationMonth: string;
};

type PcpOption = {
  id: string;
  name: string;
  npiNumber?: string;
  phone?: string;
};

const EMPTY_MEDCHECK: MedcheckData = {
  medicaidIdNumber: '',
  medicaidPayer: '',
  medicaidPlanCode: '',
  medcheckMedicaidStatus: '',
  medcheckStatus: '',
  medicaidCodes: '',
  medcheckMedicaidResponse: '',
  medcheckMedicareResponse: '',
  medcheckSyncDate: '',
  medcheckSyncStatus: '',
  medicarePlanName: '',
  recertificationMonth: '',
};

const EMPTY_FORM: FormState = {
  leadType: 'Patient',
  patientFirstName: '',
  patientLastName: '',
  patientDateOfBirth: '',
  patientMobile: '',
  patientPhone: '',
  medicaidId: '',
  pcpId: '',
  street: '',
  aptSuite: '',
  city: '',
  state: '',
  zipCode: '',
  caregiverFirstName: '',
  caregiverLastName: '',
  caregiverDateOfBirth: '',
  caregiverMobile: '',
  caregiverPhone: '',
  email: '',
  primaryLanguage: '',
  medallionNotes: '',
  medallionHealthUser: '',
};

const serializePhone = (value: string) => getPhoneDigits(value) || undefined;
const normalizeState = (value: string) => value.trim().toUpperCase();
const normalizeMedicaidId = (value: string) => value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
const isValidNewYorkMedicaidId = (value: string) => /^[A-Z]{2}\d{5}[A-Z]$/.test(normalizeMedicaidId(value));

function hasAnyPatientData(form: FormState, selectedPcp: PcpOption | null, medcheckData: MedcheckData) {
  return Boolean(
    form.patientFirstName.trim() ||
    form.patientLastName.trim() ||
    form.patientDateOfBirth.trim() ||
    form.patientMobile.trim() ||
    form.patientPhone.trim() ||
    form.medicaidId.trim() ||
    form.street.trim() ||
    form.aptSuite.trim() ||
    form.city.trim() ||
    form.state.trim() ||
    form.zipCode.trim() ||
    form.medallionNotes.trim() ||
    selectedPcp ||
    medcheckData.medicaidPayer ||
    medcheckData.medicaidPlanCode ||
    medcheckData.medcheckMedicaidStatus
  );
}

export default function QuickEntry() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentUser = getStoredUser();
  const normalizedPublicId = searchParams.get('id')?.trim() || '';
  const currentQeId = currentUser?.username?.startsWith('qe_') ? currentUser.username.slice(3) : '';
  const isCurrentPublicSession = Boolean(normalizedPublicId && currentQeId === normalizedPublicId);
  const isPublic = Boolean(normalizedPublicId && !isCurrentPublicSession);
  const hasUserParam = searchParams.has('user') || searchParams.has('USER');
  const userParam = searchParams.get('user') || searchParams.get('USER');
  const matchedMedallionUser = hasUserParam ? normalizeQuickEntryUser(userParam) : getStoredQuickEntryUser();
  const showMedallionDropdown = !hasUserParam || !matchedMedallionUser;
  const autoLoginStartedRef = useRef<string | null>(null);

  const [form, setForm] = useState<FormState>({ ...EMPTY_FORM, medallionHealthUser: matchedMedallionUser });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [addressKey, setAddressKey] = useState(0);
  const [publicLoading, setPublicLoading] = useState(Boolean(normalizedPublicId));
  const [publicError, setPublicError] = useState('');
  const [medicaidLookupId, setMedicaidLookupId] = useState('');
  const [medcheckData, setMedcheckData] = useState<MedcheckData>(EMPTY_MEDCHECK);
  const [medcheckLoading, setMedcheckLoading] = useState(false);
  const [medcheckMessage, setMedcheckMessage] = useState('');
  const [medcheckLookupComplete, setMedcheckLookupComplete] = useState(false);
  const [medicaidResetConfirmOpen, setMedicaidResetConfirmOpen] = useState(false);
  const [selectedPcp, setSelectedPcp] = useState<PcpOption | null>(null);
  const [pcpInput, setPcpInput] = useState('');
  const [pcpOptions, setPcpOptions] = useState<PcpOption[]>([]);
  const [pcpLoading, setPcpLoading] = useState(false);

  const isCaregiver = form.leadType === 'Caregiver';
  const effectiveQuickEntryUser = normalizeQuickEntryUser(form.medallionHealthUser) || matchedMedallionUser;
  const switchPortalUrl = currentQeId
    ? `https://portal.faithhealthcare.com/start?qe=true&id=${encodeURIComponent(currentQeId)}`
    : 'https://portal.faithhealthcare.com/start?qe=true';

  useEffect(() => {
    if (hasUserParam) setStoredQuickEntryUser(userParam);
  }, [hasUserParam, userParam]);

  useEffect(() => {
    if (!normalizedPublicId) {
      setPublicLoading(false);
      return;
    }
    if (isCurrentPublicSession) {
      setPublicLoading(false);
      setPublicError('');
      return;
    }
    if (autoLoginStartedRef.current === normalizedPublicId) return;

    let cancelled = false;
    autoLoginStartedRef.current = normalizedPublicId;
    setPublicLoading(true);
    setPublicError('');
    fetch('/api/auth/qe-login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ qeLoginId: normalizedPublicId }),
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok) {
          setPublicError(data.message || 'This form is not accessible at this time.');
        } else {
          storeAuthSession(data.accessToken, data.refreshToken, data.user);
          setPublicError('');
        }
      })
      .catch(() => {
        if (!cancelled) setPublicError('This form is not accessible at this time.');
      })
      .finally(() => {
        if (!cancelled) setPublicLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isCurrentPublicSession, normalizedPublicId]);

  useEffect(() => {
    if (matchedMedallionUser) {
      setForm((prev) => ({ ...prev, medallionHealthUser: matchedMedallionUser }));
    }
  }, [matchedMedallionUser]);

  useEffect(() => {
    if (isCaregiver || pcpInput.trim().length < 3 || isPublic) {
      setPcpOptions([]);
      setPcpLoading(false);
      return;
    }

    let cancelled = false;
    const timeout = window.setTimeout(() => {
      setPcpLoading(true);
      authFetch(`/api/quick-entry/pcp?search=${encodeURIComponent(pcpInput.trim())}`)
        .then(async (res) => {
          const data = await res.json().catch(() => ({}));
          if (!cancelled) setPcpOptions(res.ok ? data.records || [] : []);
        })
        .catch(() => {
          if (!cancelled) setPcpOptions([]);
        })
        .finally(() => {
          if (!cancelled) setPcpLoading(false);
        });
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [isCaregiver, isPublic, pcpInput]);

  const updateField = (name: keyof FormState, value: string | boolean) => {
    const nextValue = name === 'state' && typeof value === 'string'
      ? normalizeState(value)
      : name === 'medicaidId' && typeof value === 'string'
        ? normalizeMedicaidId(value)
        : value;

    if (name === 'medallionHealthUser' && typeof nextValue === 'string') {
      setStoredQuickEntryUser(nextValue);
    }
    if (name === 'medicaidId') {
      setMedcheckData(EMPTY_MEDCHECK);
      setMedcheckMessage('');
      setMedcheckLookupComplete(false);
    }
    setForm((prev) => ({ ...prev, [name]: nextValue }));
    setErrors((prev) => {
      const nextErrors = { ...prev, [name]: '' };
      if (typeof nextValue === 'string' && isValidPhone(nextValue)) {
        if (name === 'patientMobile' || name === 'patientPhone') {
          const sibling = name === 'patientMobile' ? 'patientPhone' : 'patientMobile';
          if (prev[sibling] === 'Enter patient mobile or phone') nextErrors[sibling] = '';
        } else if (name === 'caregiverMobile' || name === 'caregiverPhone') {
          const sibling = name === 'caregiverMobile' ? 'caregiverPhone' : 'caregiverMobile';
          if (prev[sibling] === 'Enter mobile or phone') nextErrors[sibling] = '';
        }
      }
      return nextErrors;
    });
  };

  const handleFieldChange = (name: keyof FormState) => (event: ChangeEvent<HTMLInputElement>) => {
    updateField(name, event.target.value);
  };

  const clearPatientForMedicaidReset = () => {
    setForm((prev) => ({
      ...EMPTY_FORM,
      leadType: 'Patient',
      medallionHealthUser: prev.medallionHealthUser || effectiveQuickEntryUser,
    }));
    setErrors({});
    setMedicaidLookupId('');
    setMedcheckData(EMPTY_MEDCHECK);
    setMedcheckMessage('');
    setMedcheckLookupComplete(false);
    setMedicaidResetConfirmOpen(false);
    setSelectedPcp(null);
    setPcpInput('');
    setPcpOptions([]);
    setAddressKey((key) => key + 1);
  };

  const validate = () => {
    const nextErrors: FieldErrors = {};
    if (isCaregiver) {
      if (!form.caregiverFirstName.trim()) nextErrors.caregiverFirstName = 'Required';
      if (!form.caregiverLastName.trim()) nextErrors.caregiverLastName = 'Required';
      if (!form.caregiverMobile.trim() && !form.caregiverPhone.trim()) {
        nextErrors.caregiverMobile = 'Enter mobile or phone';
        nextErrors.caregiverPhone = 'Enter mobile or phone';
      }
    } else {
      if (!form.patientFirstName.trim()) nextErrors.patientFirstName = 'Required';
      if (!form.patientLastName.trim()) nextErrors.patientLastName = 'Required';
      if (!form.patientMobile.trim() && !form.patientPhone.trim()) {
        nextErrors.patientMobile = 'Enter patient mobile or phone';
        nextErrors.patientPhone = 'Enter patient mobile or phone';
      }
      if (form.medicaidId && !isValidNewYorkMedicaidId(form.medicaidId)) {
        nextErrors.medicaidId = 'Enter a NY Medicaid CIN, like AB12345C';
      }
    }
    const phoneFields = isCaregiver
      ? ['caregiverMobile', 'caregiverPhone'] as const
      : ['patientMobile', 'patientPhone'] as const;
    for (const field of phoneFields) {
      if (form[field].trim() && !isValidPhone(form[field])) nextErrors[field] = PHONE_INPUT_ERROR;
    }
    if (form.state && !/^[A-Z]{2}$/.test(form.state)) nextErrors.state = 'Enter a 2-letter state code';
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleLeadTypeChange = (_event: React.MouseEvent<HTMLElement>, newValue: LeadType | null) => {
    if (!newValue) return;
    setForm({ ...EMPTY_FORM, leadType: newValue, medallionHealthUser: effectiveQuickEntryUser });
    setErrors({});
    setMedicaidLookupId('');
    setMedcheckData(EMPTY_MEDCHECK);
    setMedcheckMessage('');
    setMedcheckLookupComplete(false);
    setSelectedPcp(null);
    setPcpInput('');
    setPcpOptions([]);
    setAddressKey((key) => key + 1);
  };

  const handleMedicaidLookup = async () => {
    const medicaidId = normalizeMedicaidId(medicaidLookupId);
    setErrorMsg('');
    setMedcheckMessage('');
    if (!medicaidId) {
      setErrors((prev) => ({ ...prev, medicaidId: 'Required' }));
      return;
    }
    if (!isValidNewYorkMedicaidId(medicaidId)) {
      setErrors((prev) => ({ ...prev, medicaidId: 'Enter a NY Medicaid CIN, like AB12345C' }));
      return;
    }
    setMedcheckLoading(true);
    setMedcheckData(EMPTY_MEDCHECK);
    try {
      const response = isPublic
        ? await fetch(`/api/quick-entry/public/medcheck?id=${encodeURIComponent(normalizedPublicId)}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ medicaidId }),
          })
        : await authFetch('/api/quick-entry/medcheck', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ medicaidId }),
          });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'Unable to check Medicaid eligibility.');

      const result = data.result || {};
      const nextMedcheckData: MedcheckData = {
        medicaidIdNumber: result.medicaidIdNumber || medicaidId,
        medicaidPayer: result.medicaidPayer || '',
        medicaidPlanCode: result.medicaidPlanCode || '',
        medcheckMedicaidStatus: result.medcheckMedicaidStatus || '',
        medcheckStatus: result.medcheckStatus || '',
        medicaidCodes: result.medicaidCodes || '',
        medcheckMedicaidResponse: result.medcheckMedicaidResponse || '',
        medcheckMedicareResponse: result.medcheckMedicareResponse || '',
        medcheckSyncDate: result.medcheckSyncDate || '',
        medcheckSyncStatus: result.medcheckSyncStatus || '',
        medicarePlanName: result.medicarePlanName || '',
        recertificationMonth: result.recertificationMonth || '',
      };
      setMedcheckData(nextMedcheckData);
      setForm((prev) => ({
        ...prev,
        patientFirstName: result.firstName || prev.patientFirstName,
        patientLastName: result.lastName || prev.patientLastName,
        patientDateOfBirth: result.dateOfBirth || prev.patientDateOfBirth,
        medicaidId: nextMedcheckData.medicaidIdNumber || medicaidId,
      }));
      setErrors((prev) => ({ ...prev, medicaidId: '' }));
      setMedicaidLookupId('');
      setMedcheckLookupComplete(true);
    } catch (error) {
      setMedcheckMessage(error instanceof Error ? error.message : 'Unable to check Medicaid eligibility.');
    } finally {
      setMedcheckLoading(false);
    }
  };

  const payload = useMemo(() => {
    if (isCaregiver) {
      return {
        leadType: 'Caregiver',
        firstName: form.caregiverFirstName.trim(),
        lastName: form.caregiverLastName.trim(),
        mobilePhone: serializePhone(form.caregiverMobile),
        phone: serializePhone(form.caregiverPhone),
        dateOfBirth: form.caregiverDateOfBirth || undefined,
        street: form.street.trim() || undefined,
        aptSuite: form.aptSuite.trim() || undefined,
        city: form.city.trim() || undefined,
        state: form.state.trim() || undefined,
        zipCode: form.zipCode.trim() || undefined,
        email: form.email.trim() || undefined,
        primaryLanguage: form.primaryLanguage || undefined,
        medallionNotes: form.medallionNotes.trim() || undefined,
        medallionHealthUser: form.medallionHealthUser || undefined,
      };
    }
    return {
      leadType: 'Patient',
      firstName: form.patientFirstName.trim(),
      lastName: form.patientLastName.trim(),
      mobilePhone: serializePhone(form.patientMobile),
      phone: serializePhone(form.patientPhone),
      dateOfBirth: form.patientDateOfBirth || undefined,
      medicaidId: form.medicaidId.trim() || medcheckData.medicaidIdNumber || undefined,
      ...medcheckData,
      runMedcheckSync: medcheckData.medcheckSyncStatus ? false : undefined,
      street: form.street.trim() || undefined,
      aptSuite: form.aptSuite.trim() || undefined,
      city: form.city.trim() || undefined,
      state: form.state.trim() || undefined,
      zipCode: form.zipCode.trim() || undefined,
      pcpId: selectedPcp?.id || form.pcpId || undefined,
      medallionNotes: form.medallionNotes.trim() || undefined,
      medallionHealthUser: form.medallionHealthUser || undefined,
    };
  }, [form, isCaregiver, medcheckData, selectedPcp]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setErrorMsg('');
    if (!validate()) return;
    if (isPublic && publicError) return;
    setSubmitting(true);
    try {
      const response = isPublic
        ? await fetch(`/api/quick-entry/public?id=${encodeURIComponent(normalizedPublicId)}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          })
        : await authFetch('/api/quick-entry', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setErrorMsg(data.message || 'Unable to create lead.');
        return;
      }
      setSuccessOpen(true);
      setForm({ ...EMPTY_FORM, leadType: form.leadType, medallionHealthUser: effectiveQuickEntryUser });
      setErrors({});
      setMedicaidLookupId('');
      setMedcheckData(EMPTY_MEDCHECK);
      setMedcheckMessage('');
      setMedcheckLookupComplete(false);
      setSelectedPcp(null);
      setPcpInput('');
      setPcpOptions([]);
      setAddressKey((key) => key + 1);
    } catch (error) {
      setErrorMsg(error instanceof Error ? error.message : 'Unable to create lead.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseSuccessToast = (_event?: SyntheticEvent | Event, reason?: string) => {
    if (reason !== 'clickaway') setSuccessOpen(false);
  };

  if (isPublic && publicLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (isPublic && publicError) {
    return (
      <Container maxWidth="sm" sx={{ mt: 5 }}>
        <Grow in timeout={800}>
          <Paper variant="outlined" sx={{ p: { xs: 3, sm: 5 }, textAlign: 'center', backgroundColor: '#fff', borderRadius: 1 }}>
            <CancelOutlinedIcon sx={{ fontSize: 64, color: '#8900e1' }} />
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>Oops!</Typography>
            <Typography variant="h6" color="text.secondary">{publicError}</Typography>
          </Paper>
        </Grow>
      </Container>
    );
  }

  if (!isPublic && (!currentUser || (!hasRole(currentUser, 'quick-entry') && !hasRole(currentUser, 'admin')))) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <Typography variant="h6" color="error">Access denied.</Typography>
      </Box>
    );
  }

  const medcheckDisplay = Boolean(
    medcheckData.medicaidPlanCode ||
    medcheckData.medicaidPayer ||
    medcheckData.medcheckMedicaidStatus
  );

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 5 }, mb: 8 }}>
      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, bgcolor: '#fff', borderRadius: 1 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography variant="h5" component="h1" sx={{ fontWeight: 700, color: '#330662' }}>
              New Lead
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {isCaregiver ? 'Caregiver' : 'Patient'} intake
            </Typography>
          </Box>
          <ToggleButtonGroup value={form.leadType} exclusive onChange={handleLeadTypeChange} size="small">
            {(['Patient', 'Caregiver'] as const).map((type) => (
              <ToggleButton
                key={type}
                value={type}
                sx={{ px: 3, textTransform: 'none', '&.Mui-selected': { backgroundColor: '#8600e1', color: '#fff' } }}
              >
                {type}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>

        <form onSubmit={handleSubmit}>
          {!isCaregiver && (
            <FormSection
              title="Medicaid Check"
              sx={{ mb: 3 }}
              action={medcheckLookupComplete && (
                <Button
                  type="button"
                  variant="outlined"
                  sx={{ borderColor: '#8600e1', color: '#8600e1', textTransform: 'none', whiteSpace: 'nowrap' }}
                  onClick={() => (
                    hasAnyPatientData(form, selectedPcp, medcheckData)
                      ? setMedicaidResetConfirmOpen(true)
                      : clearPatientForMedicaidReset()
                  )}
                >
                  Check Another Medicaid Number
                </Button>
              )}
            >
              {!medcheckLookupComplete && (
                <Box display="flex" gap={1} alignItems="flex-start" sx={{ maxWidth: 640, flexDirection: { xs: 'column', sm: 'row' } }}>
                  <FormTextField
                    label="Medicaid ID to Check"
                    inputMask="aa99999a"
                    maskChar={null}
                    fullWidth
                    value={medicaidLookupId}
                    onChange={(event) => {
                      setMedicaidLookupId(normalizeMedicaidId(event.target.value));
                      setMedcheckMessage('');
                      setErrors((prev) => ({ ...prev, medicaidId: '' }));
                    }}
                    error={!!errors.medicaidId}
                    helperText={errors.medicaidId || 'NY Medicaid CIN, like AB12345C'}
                  />
                  <Button
                    type="button"
                    variant="contained"
                    sx={{ height: 56, minWidth: 160, backgroundColor: '#8600e1', textTransform: 'none' }}
                    disabled={medcheckLoading || submitting || !medicaidLookupId.trim()}
                    onClick={handleMedicaidLookup}
                  >
                    {medcheckLoading ? <CircularProgress size={20} color="inherit" /> : 'Check Medicaid'}
                  </Button>
                </Box>
              )}
              {medcheckDisplay && (
                <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'grey.50' }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary">Medicaid Plan Code</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>{medcheckData.medicaidPlanCode || 'Not returned'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary">Medicaid Payer</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>{medcheckData.medicaidPayer || 'Not returned'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary">MedCheck Medicaid Status</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>{medcheckData.medcheckMedicaidStatus || 'Not returned'}</Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}
              {medcheckMessage && <Alert severity="error" sx={{ mt: 2 }}>{medcheckMessage}</Alert>}
            </FormSection>
          )}

          <FormSection title={isCaregiver ? 'Caregiver Details' : 'Patient Details'} icon={<PersonIcon />} sx={{ mb: 3 }}>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormTextField
                    label={isCaregiver ? 'First Name' : 'Patient First Name'}
                    fullWidth
                    required
                    value={isCaregiver ? form.caregiverFirstName : form.patientFirstName}
                    error={!!(isCaregiver ? errors.caregiverFirstName : errors.patientFirstName)}
                    helperText={(isCaregiver ? errors.caregiverFirstName : errors.patientFirstName) || ''}
                    onChange={handleFieldChange(isCaregiver ? 'caregiverFirstName' : 'patientFirstName')}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormTextField
                    label={isCaregiver ? 'Last Name' : 'Patient Last Name'}
                    fullWidth
                    required
                    value={isCaregiver ? form.caregiverLastName : form.patientLastName}
                    error={!!(isCaregiver ? errors.caregiverLastName : errors.patientLastName)}
                    helperText={(isCaregiver ? errors.caregiverLastName : errors.patientLastName) || ''}
                    onChange={handleFieldChange(isCaregiver ? 'caregiverLastName' : 'patientLastName')}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <PhoneTextField
                    key={form.leadType}
                    label={isCaregiver ? 'Mobile Phone' : 'Patient Mobile'}
                    fullWidth
                    value={isCaregiver ? form.caregiverMobile : form.patientMobile}
                    error={!!(isCaregiver ? errors.caregiverMobile : errors.patientMobile)}
                    helperText={(isCaregiver ? errors.caregiverMobile : errors.patientMobile) || ''}
                    onValueChange={(value) => updateField(isCaregiver ? 'caregiverMobile' : 'patientMobile', value)}
                    invalidPhoneMessage={PHONE_INPUT_ERROR}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DateField
                    label={isCaregiver ? 'Date of Birth' : 'Patient Date of Birth'}
                    fullWidth
                    value={isCaregiver ? form.caregiverDateOfBirth : form.patientDateOfBirth}
                    onChange={handleFieldChange(isCaregiver ? 'caregiverDateOfBirth' : 'patientDateOfBirth')}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <PhoneTextField
                    key={form.leadType}
                    label={isCaregiver ? 'Phone' : 'Patient Phone'}
                    fullWidth
                    value={isCaregiver ? form.caregiverPhone : form.patientPhone}
                    error={!!(isCaregiver ? errors.caregiverPhone : errors.patientPhone)}
                    helperText={(isCaregiver ? errors.caregiverPhone : errors.patientPhone) || ''}
                    onValueChange={(value) => updateField(isCaregiver ? 'caregiverPhone' : 'patientPhone', value)}
                    invalidPhoneMessage={PHONE_INPUT_ERROR}
                  />
                </Grid>
                {isCaregiver ? (
                  <>
                    <Grid item xs={12} sm={6}>
                      <FormTextField label="Email" fullWidth value={form.email} onChange={handleFieldChange('email')} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <MuiTextField
                        select
                        label="Primary Language"
                        fullWidth
                        value={form.primaryLanguage}
                        onChange={(event) => updateField('primaryLanguage', event.target.value)}
                      >
                        <MenuItem value=""><em>None</em></MenuItem>
                        {CAREGIVER_PRIMARY_LANGUAGE_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                        ))}
                      </MuiTextField>
                    </Grid>
                  </>
                ) : (
                  <>
                    <Grid item xs={12} sm={6}>
                      <FormTextField
                        label="Medicaid ID Number"
                        inputMask="aa99999a"
                        maskChar={null}
                        fullWidth
                        value={form.medicaidId}
                        onChange={handleFieldChange('medicaidId')}
                        error={!!errors.medicaidId}
                        helperText={errors.medicaidId || 'NY Medicaid CIN, like AB12345C'}
                        readOnly={medcheckLookupComplete}
                      />
                    </Grid>
                    {!isPublic && (
                      <Grid item xs={12}>
                        <Autocomplete
                          options={pcpOptions}
                          getOptionLabel={(option) => option?.name || ''}
                          isOptionEqualToValue={(option, value) => option?.id === value?.id}
                          value={selectedPcp}
                          inputValue={pcpInput}
                          onInputChange={(_, value) => setPcpInput(value)}
                          onChange={(_, value) => {
                            setSelectedPcp(value);
                            updateField('pcpId', value?.id || '');
                          }}
                          loading={pcpLoading}
                          filterOptions={(options) => options}
                          noOptionsText={pcpInput.length < 3 ? 'Type at least 3 characters to search' : 'No results found'}
                          renderOption={(props, option) => (
                            <li {...props} key={option.id}>
                              <Box>
                                <Typography variant="body1">{option.name}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {[option.npiNumber ? `NPI: ${option.npiNumber}` : null, option.phone].filter(Boolean).join(' - ')}
                                </Typography>
                              </Box>
                            </li>
                          )}
                          renderInput={(params) => (
                            <MuiTextField {...params} label="PCP" placeholder="Search by name, phone, or NPI" helperText="Type at least 3 characters to search by name, phone, or NPI" />
                          )}
                        />
                      </Grid>
                    )}
                  </>
                )}
                <Grid item xs={12}>
                  <AddressAutocomplete
                    key={addressKey}
                    onAddressSelect={({ street, aptSuite, city, state, zipCode }) => {
                      setForm((prev) => ({ ...prev, street, aptSuite, city, state: normalizeState(state), zipCode }));
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormTextField label="Street" fullWidth value={form.street} onChange={handleFieldChange('street')} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormTextField label="Apt / Suite" fullWidth value={form.aptSuite} onChange={handleFieldChange('aptSuite')} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormTextField label="City" fullWidth value={form.city} onChange={handleFieldChange('city')} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormTextField label="State" inputMask="aa" maskChar={null} fullWidth value={form.state} onChange={handleFieldChange('state')} error={!!errors.state} helperText={errors.state || ''} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormTextField label="Postal Code" fullWidth value={form.zipCode} onChange={handleFieldChange('zipCode')} />
                </Grid>
            </Grid>
          </FormSection>

          {showMedallionDropdown && (
            <FormSection title="Created By" sx={{ mb: 3 }}>
              <MuiTextField select label="Created By" fullWidth value={form.medallionHealthUser} onChange={(event) => updateField('medallionHealthUser', event.target.value)}>
                <MenuItem value=""><em>None</em></MenuItem>
                {MEDALLION_USERS.map((name) => <MenuItem key={name} value={name}>{name}</MenuItem>)}
              </MuiTextField>
            </FormSection>
          )}

          <FormSection title="Notes" icon={<NotesIcon />} sx={{ mb: 3 }}>
            <FormTextField label="Important Additional Info" fullWidth multiline minRows={4} value={form.medallionNotes} onChange={handleFieldChange('medallionNotes')} />
          </FormSection>

          {errorMsg && <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>}

          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
              {!isPublic && <Button variant="outlined" sx={{ textTransform: 'none' }} onClick={() => navigate('/qe-landing')}>Back to Landing</Button>}
              <Button
                variant="outlined"
                sx={{ textTransform: 'none' }}
                onClick={() => {
                  const destinationUrl = isPublic
                    ? `https://portal.faithhealthcare.com/quick-entry${searchParams.toString() ? `?${searchParams.toString()}` : ''}`
                    : switchPortalUrl;
                  window.open(appendQuickEntryUserToUrl(destinationUrl, effectiveQuickEntryUser), '_blank', 'noopener,noreferrer');
                }}
              >
                Switch to Florida
              </Button>
            </Stack>
            <Button type="submit" variant="contained" sx={{ textTransform: 'none' }} disabled={submitting || (isPublic && Boolean(publicError))}>
              {submitting ? <CircularProgress size={24} color="inherit" /> : isCaregiver ? 'Submit Caregiver Lead' : 'Submit Lead'}
            </Button>
          </Stack>
        </form>

        <Dialog open={medicaidResetConfirmOpen} onClose={() => setMedicaidResetConfirmOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Clear patient form?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Checking another Medicaid number will clear the patient form and remove the current Medicaid check results.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button sx={{ textTransform: 'none' }} onClick={() => setMedicaidResetConfirmOpen(false)}>Back</Button>
            <Button variant="contained" sx={{ backgroundColor: '#8600e1', textTransform: 'none' }} onClick={clearPatientForMedicaidReset}>
              Clear and Check Another
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar open={successOpen} autoHideDuration={4000} onClose={handleCloseSuccessToast} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
          <Alert onClose={handleCloseSuccessToast} severity="success" sx={{ width: '100%' }}>
            {isCaregiver ? 'Caregiver lead created successfully.' : 'Lead created successfully.'}
          </Alert>
        </Snackbar>
      </Paper>
    </Container>
  );
}
